'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * One-shot "has this scrolled into view yet" flag, used to fade sections in.
 *
 * This is deliberately fail-safe. The reveal it drives sets `opacity: 0` until
 * it flips, so ANY path where the trigger never fires leaves real content
 * permanently invisible — a blank page, not a missing animation. The five
 * near-identical copies this replaces could each get stuck that way:
 *
 *   - `threshold: 0.15`-style ratios are a fraction of the ELEMENT's area, so
 *     an element taller than ~1/threshold viewports can never reach the ratio
 *     and never fires at all.
 *   - IntersectionObserver samples on a cadence rather than continuously. Fast
 *     scrolling, `scrollIntoView`, an anchor jump, or the browser restoring
 *     scroll position on reload can take an element from below the fold to
 *     above it between two samples, so `isIntersecting` is never once true.
 *     The old hooks disconnected on first hit and had no other route to
 *     `true`, so the section stayed at opacity 0 for the rest of the session.
 *
 * Three independent things now have to fail before content stays hidden:
 *   1. A geometry check on mount — anything already in or past view reveals
 *      without waiting for a callback at all.
 *   2. IntersectionObserver at `threshold: 0` with a negative-bottom
 *      `rootMargin` — fires on any overlap, whatever the element's height.
 *   3. A shared, rAF-throttled scroll/resize sweep over everything still
 *      pending. This is what catches a missed sample: the element reveals the
 *      moment the viewport actually reaches it, regardless of what the
 *      observer did or didn't report.
 *
 * There is deliberately NO blanket "reveal everything after N seconds" timer.
 * That was the first version of this fix and it was worse than the bug on
 * dense pages: it revealed every section on the page simultaneously, and
 * pages that start work on reveal (the product pages kick off a 60Hz count-up
 * per statistic) got that work all at once in one long task. Revealing on
 * approach keeps the cost spread across the scroll, where it belongs.
 *
 * Reduced motion and environments without IntersectionObserver reveal
 * immediately.
 */

/** How far beyond the viewport counts as "reached", for the scroll sweep. */
const APPROACH_PX = 120

type Pending = { el: Element; reveal: () => void }

const pending = new Set<Pending>()
let sweepScheduled = false
let listening = false

function runSweep() {
  sweepScheduled = false
  if (pending.size === 0) return
  const vh = window.innerHeight
  for (const entry of pending) {
    const r = entry.el.getBoundingClientRect()
    // Reached it, or already scrolled past it.
    if (r.top < vh + APPROACH_PX && r.bottom > -APPROACH_PX) {
      pending.delete(entry)
      entry.reveal()
    } else if (r.bottom <= 0) {
      pending.delete(entry)
      entry.reveal()
    }
  }
  if (pending.size === 0) stopListening()
}

function scheduleSweep() {
  if (sweepScheduled) return
  sweepScheduled = true
  requestAnimationFrame(runSweep)
}

function startListening() {
  if (listening) return
  listening = true
  window.addEventListener('scroll', scheduleSweep, { passive: true })
  window.addEventListener('resize', scheduleSweep, { passive: true })
}

function stopListening() {
  if (!listening) return
  listening = false
  window.removeEventListener('scroll', scheduleSweep)
  window.removeEventListener('resize', scheduleSweep)
}

export function useReveal<T extends Element = HTMLDivElement>(
  /**
   * Kept for call-site compatibility. It is intentionally NOT used as an
   * intersection ratio — see above for why ratios strand tall sections.
   * Larger values simply trigger a little later, via rootMargin.
   */
  threshold = 0
) {
  const ref = useRef<T>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    // No node to watch (conditional render, or a ref that never landed).
    // Content must not stay hidden on account of it.
    if (!el) {
      setShown(true)
      return
    }

    let done = false
    const reveal = () => {
      if (done) return
      done = true
      setShown(true)
    }

    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      reveal()
      return
    }

    // (1) Already in view, or already scrolled past, at mount.
    const r = el.getBoundingClientRect()
    if (r.top < window.innerHeight && r.bottom > 0) {
      reveal()
      return
    }
    if (r.bottom <= 0) {
      reveal()
      return
    }

    // `threshold` is repurposed: bigger values wait until the element is a
    // little further into the viewport, without ever gating on its area.
    const bottomInset = Math.round(Math.min(Math.max(threshold, 0), 0.4) * 100)

    // (2) Primary trigger.
    const ob = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.boundingClientRect.bottom <= 0) {
            reveal()
            return
          }
        }
      },
      { threshold: 0, rootMargin: `0px 0px -${bottomInset}px 0px` }
    )
    ob.observe(el)

    // (3) Backstop for samples the observer never delivers.
    const entry: Pending = { el, reveal }
    pending.add(entry)
    startListening()

    return () => {
      done = true
      ob.disconnect()
      pending.delete(entry)
      if (pending.size === 0) stopListening()
    }
  }, [threshold])

  return { ref, shown }
}

/** Tuple-shaped variant, for the call sites written against that signature. */
export function useInViewTuple<T extends Element = HTMLElement>(
  threshold = 0
): [React.RefObject<T | null>, boolean] {
  const { ref, shown } = useReveal<T>(threshold)
  return [ref, shown]
}
